import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useAgentName } from './agentName'

interface AgentAvatarProps {
  onClick?: () => void
  /** Held open — the avatar reads as the active surface while its panel is up. */
  active?: boolean
  /** An unread proactive message: the agent spoke first and has not been read. */
  unread?: boolean
  className?: string
}

/**
 * The agent, as a face rather than a text field. Muse keeps its avatar centred
 * in the top bar and visibly alive, so the agent reads as someone present on
 * every tab rather than a tool you go and open.
 */
export function AgentAvatar({ onClick, active, unread, className }: AgentAvatarProps) {
  const agentName = useAgentName()

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Ask ${agentName}`}
      aria-expanded={active}
      className={cn('flex flex-col items-center', className)}
    >
      <span className="relative flex size-11 items-center justify-center">
        {/* The "in motion" half of the Muse pattern: it is awake even when idle. */}
        <span className="bg-brand-200 absolute inset-0 animate-pulse rounded-lg" aria-hidden />
        <Avatar
          className={cn(
            'relative size-11 rounded-lg border-4 border-white shadow-md transition-shadow',
            active && 'shadow-lg'
          )}
        >
          {/* Empty on purpose: Bluey is a character, and none is drawn yet. */}
          <AvatarFallback className="bg-brand-100 rounded-lg" />
        </Avatar>
        {unread && (
          <span
            className="bg-destructive absolute top-0 right-0 size-3 rounded-full border-2 border-white"
            aria-label="1 unread message"
            role="status"
          />
        )}
      </span>
      {/* Lifted over the avatar's lower edge, so the two read as one object. */}
      <span className="text-ink relative z-10 -mt-1.5 rounded-full border border-white/60 bg-white/50 px-2 py-0.5 text-[10px] leading-none font-medium shadow-sm backdrop-blur-md">
        {agentName}
      </span>
    </button>
  )
}
